import { formatShortDate } from "@clipboard-health/date-time";
import { PromotionBanner } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { type PaymentsListItem } from "@src/appV2/redesign/Payments/Earnings/types/PaymentsListItem";
import { PAYMENT_DETAILS_FULL_PATH } from "@src/appV2/redesign/Payments/PaymentDetails/paths";
import { PAST_SHIFT_DETAILS_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import {
  ExtraWorkedTimeRequestStatus,
  ExtraWorkedTimeRequestStatusUpdater,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { isAfter, parseISO } from "date-fns";
import { generatePath, useHistory } from "react-router-dom";

import { useGetWorkerPaymentsByShift } from "../../PastShiftDetails/api/useGetWorkerPaymentsByShift";

interface ExtraWorkedTimeRequestBannerProps {
  status: ExtraWorkedTimeRequestStatus.APPROVED | ExtraWorkedTimeRequestStatus.DECLINED;
  workplaceName: string;
  workplaceTimezone: string;
  shiftDate: Date;
  shiftId: string;
  updatedAt: string;
  isVerified: boolean;
  isFullShiftPayment?: boolean;
  onDismiss: () => void;
  statusUpdatedBy?: ExtraWorkedTimeRequestStatusUpdater;
  statusUpdateReason?: string | null;
}

function getBannerContent(params: {
  isApproved: boolean;
  isExpired: boolean;
  isVerified: boolean;
  isFullShiftPayment?: boolean;
  workplaceName: string;
  shiftDate: Date;
  workplaceTimezone: string;
}): { title: string; subtitle: string } {
  const {
    isApproved,
    isExpired,
    isVerified,
    isFullShiftPayment,
    workplaceName,
    shiftDate,
    workplaceTimezone,
  } = params;

  const isFullShiftPaymentRequest = isFullShiftPayment ?? !isVerified;
  const formattedDate = formatShortDate(shiftDate, { timeZone: workplaceTimezone });

  if (isApproved && isFullShiftPaymentRequest) {
    return {
      title: "Good news! Your full shift payment was approved.",
      subtitle: `${workplaceName} approved your full shift payment for your shift on ${formattedDate}.`,
    };
  }

  if (isApproved) {
    return {
      title: "Good news! Your pay adjustment was approved.",
      subtitle: `${workplaceName} approved your pay adjustment request for your shift on ${formattedDate}.`,
    };
  }

  if (isExpired && isFullShiftPaymentRequest) {
    return {
      title: "Your shift payment request expired.",
      subtitle: `Your shift payment request for your ${formattedDate} shift at ${workplaceName} wasn't reviewed and may have been missed.`,
    };
  }

  if (isExpired) {
    return {
      title: "Your pay adjustment request expired.",
      subtitle: `Your pay adjustment request for your ${formattedDate} shift at ${workplaceName} wasn't reviewed and may have been missed.`,
    };
  }

  if (isFullShiftPaymentRequest) {
    return {
      title: "Your shift payment request was declined.",
      subtitle: `${workplaceName} declined your shift payment request for your shift on ${formattedDate}.`,
    };
  }

  return {
    title: "Your pay adjustment request was declined.",
    subtitle: `${workplaceName} declined your pay adjustment request for your shift on ${formattedDate}.`,
  };
}

// Temporary heuristic to find the payment associated with a missed punch request.
// This will be replaced once we can deterministically infer the payment ID directly from the missed punch request ID.
function findMissedPunchRequestPayment(
  payments: PaymentsListItem[],
  updatedAt: string
): PaymentsListItem | undefined {
  const updatedAtDate = parseISO(updatedAt);

  const eligiblePayments = payments.filter((payment) => {
    const { payoutStatus, description, createdAt } = payment.attributes;

    if (payoutStatus !== "paid") {
      return false;
    }

    if (!description.toLowerCase().includes("missed punch payment")) {
      return false;
    }

    return isAfter(parseISO(createdAt), updatedAtDate);
  });

  if (eligiblePayments.length === 0) {
    return undefined;
  }

  return eligiblePayments.reduce((closest, current) => {
    const closestDiff = parseISO(closest.attributes.createdAt).getTime() - updatedAtDate.getTime();
    const currentDiff = parseISO(current.attributes.createdAt).getTime() - updatedAtDate.getTime();
    return currentDiff < closestDiff ? current : closest;
  });
}

export function ExtraWorkedTimeRequestBanner(props: ExtraWorkedTimeRequestBannerProps) {
  const {
    status,
    workplaceName,
    workplaceTimezone,
    shiftDate,
    shiftId,
    updatedAt,
    isVerified,
    isFullShiftPayment,
    statusUpdatedBy,
    statusUpdateReason,
    onDismiss,
  } = props;

  const history = useHistory();
  const isApproved = status === ExtraWorkedTimeRequestStatus.APPROVED;
  const isExpired = !isApproved && statusUpdatedBy === ExtraWorkedTimeRequestStatusUpdater.SYSTEM;
  const isShiftHistoryPageRedesignEnabled = useCbhFlag(
    CbhFeatureFlag.ENABLE_SHIFT_HISTORY_PAGE_REDESIGN,
    { defaultValue: false }
  );

  const { data: payments, isLoading: isLoadingPayments } = useGetWorkerPaymentsByShift(shiftId, {
    enabled: isApproved && isShiftHistoryPageRedesignEnabled,
  });

  const payment = isDefined(payments)
    ? findMissedPunchRequestPayment(payments, updatedAt)
    : undefined;
  const showApprovedCta =
    isShiftHistoryPageRedesignEnabled && isApproved && (isLoadingPayments || isDefined(payment));
  const showDeclinedCta = isShiftHistoryPageRedesignEnabled && !isApproved;
  const showCta = showApprovedCta || showDeclinedCta;

  const showDeclineReason = !isApproved && !isExpired && isDefined(statusUpdateReason);

  const { title, subtitle } = getBannerContent({
    isApproved,
    isExpired,
    isVerified,
    isFullShiftPayment,
    workplaceName,
    shiftDate,
    workplaceTimezone,
  });

  return (
    <PromotionBanner
      title={title}
      subtitle={subtitle}
      icon={
        isApproved ? "clock-success-with-dollar-colored" : "clock-error-with-exclamation-colored"
      }
      borderVariant="none"
      backgroundColor={(theme) =>
        isApproved ? theme.palette.payment!.background : theme.palette.intent!.error.gradient!
      }
      titleColor={(theme) =>
        isApproved ? theme.palette.payment!.text : theme.palette.intent!.error.text
      }
      {...(showApprovedCta && {
        ctaButtonText: "View payment details",
        ctaButtonVariant: "outlined" as const,
        ctaButtonIsLoading: isLoadingPayments,
      })}
      {...(showDeclinedCta && {
        ctaButtonText: "View details & resubmit",
        ctaButtonVariant: "outlined" as const,
      })}
      sx={(theme) => ({
        width: "100%",
        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.08)",
        alignItems: "stretch",
        "& *": {
          whiteSpace: "normal",
          wordWrap: "break-word",
        },
        "& .MuiCardContent-root": {
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(3),
        },
        // Pin CTA button to bottom of card
        "& .MuiCardContent-root > .MuiButton-root": {
          marginTop: "auto",
        },
        // Convert icon+text row to grid so subtitle aligns with icon
        "& .MuiCardContent-root > .MuiStack-root > .MuiStack-root:first-of-type": {
          display: "grid",
          gridTemplateColumns: showDeclineReason ? "auto 1fr auto" : "auto 1fr",
          columnGap: showDeclineReason ? 0 : theme.spacing(4),
          rowGap: theme.spacing(3),
          alignItems: "center",
          // Decline reason: ::before = opening quote + text (truncated), ::after = closing quote (fixed)
          ...(showDeclineReason && {
            "&::before": {
              content: `"\u201C${statusUpdateReason}"`,
              gridRow: 3,
              gridColumn: "1 / 3",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              marginBottom: theme.spacing(2),
              fontSize: theme.typography.body2.fontSize,
              color: theme.palette.intent!.error.text,
              fontWeight: 500,
            },
            "&::after": {
              content: '"\u201D"',
              gridRow: 3,
              gridColumn: 3,
              marginBottom: theme.spacing(2),
              fontSize: theme.typography.body2.fontSize,
              color: theme.palette.intent!.error.text,
              fontWeight: 500,
            },
          }),
        },
        // Dissolve text column so title & subtitle become direct grid children
        "& .MuiCardContent-root > .MuiStack-root > .MuiStack-root:first-of-type > .MuiStack-root": {
          display: "contents",
        },
        "& .MuiTypography-body1": {
          fontSize: theme.typography.h6.fontSize,
          lineHeight: theme.typography.h6.lineHeight,
          ...(showDeclineReason && { marginLeft: theme.spacing(4) }),
        },
        // Subtitle spans full grid width (aligns with icon, not title)
        "& .MuiTypography-body2": {
          gridColumn: "1 / -1",
        },
      })}
      {...(showCta && {
        onClick: () => {
          if (showApprovedCta && payment) {
            onDismiss();
            history.push(generatePath(PAYMENT_DETAILS_FULL_PATH, { paymentId: payment.id }));
          } else if (showDeclinedCta) {
            onDismiss();
            history.push(generatePath(PAST_SHIFT_DETAILS_FULL_PATH, { shiftId }));
          }
        },
      })}
    />
  );
}
